import type { SaveStore } from "../services/save/store.ts";
import type { NewOutboxEntry, OutboxEntry, SaveData } from "../services/save/types.ts";
import { appStore } from "./store.ts";

let saveStore: SaveStore | null = null;
let writeChain: Promise<unknown> = Promise.resolve();

export function initPersistence(store: SaveStore): void {
  saveStore = store;
}

function requireStore(): SaveStore {
  if (!saveStore) throw new Error("persistence not initialised");
  return saveStore;
}

export function getSave(): SaveData {
  const save = appStore.get().save;
  if (!save) throw new Error("save not loaded");
  return save;
}

/** Writes are serialised so a slow write never lands after a newer one. */
function enqueue<T>(task: () => Promise<T>): Promise<T> {
  const run = writeChain.then(task, task);
  writeChain = run.catch(() => undefined);
  return run;
}

export async function updateSave(update: (save: SaveData) => SaveData): Promise<SaveData> {
  const next = update(getSave());
  appStore.set((state) => ({ ...state, save: next }));
  await enqueue(() => requireStore().writeSave(next));
  return next;
}

export async function replaceSave(save: SaveData): Promise<void> {
  appStore.set((state) => ({ ...state, save, outbox: [] }));
  await enqueue(() => requireStore().replaceAll(save));
}

export async function refreshOutbox(): Promise<readonly OutboxEntry[]> {
  const outbox = await requireStore().listOutbox();
  outbox.sort((a, b) => a.houseId.localeCompare(b.houseId) || a.levelId - b.levelId || a.createdAt - b.createdAt);
  appStore.set((state) => ({ ...state, outbox }));
  return outbox;
}

/** Stores the proof and updated progress atomically, before any success UI. */
export async function commitResult(entry: NewOutboxEntry, update: (save: SaveData) => SaveData): Promise<OutboxEntry> {
  const next = update(getSave());
  const stored = await enqueue(() => requireStore().commitResult(entry, next));
  appStore.set((state) => ({ ...state, save: next }));
  await refreshOutbox();
  return stored;
}

export async function removeOutbox(ids: readonly number[]): Promise<void> {
  if (ids.length === 0) return;
  await enqueue(() => requireStore().removeOutbox(ids));
  await refreshOutbox();
}

export async function putOutbox(entries: readonly OutboxEntry[]): Promise<void> {
  if (entries.length === 0) return;
  await enqueue(() => requireStore().putOutbox(entries));
  await refreshOutbox();
}

export async function backupLocal(): Promise<void> {
  const state = appStore.get();
  if (state.save) await requireStore().backup(state.save, state.outbox);
}

// Singletons (store, services, listeners) must never be duplicated by a hot update.
if (import.meta.hot) import.meta.hot.accept(() => window.location.reload());
